import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

// Components
import ProtectedRoute from '../components/ProtectedRoute'
import WelcomePage from '../components/WelcomePage'
import LoginForm from '../../features/auth/components/LoginForm'
import { SignUpForm, AccountCompletionForm } from '../../features/invitations'
import ISODashboard from '../../features/iso-dashboard/components/Dashboard'
import AdminDashboard from '../../features/admin-dashboard/components/AdminDashboard'
import ISODetailPage from '../../features/admin-dashboard/pages/ISODetailPage'
import { ResetPasswordForm } from '../../features/forgot-password'
import { SubmitDealApplication } from '../../features/iso-submit-deal'
import { ChatterPage } from '../../features/chatter'


// Component to handle redirects based on user role
const DashboardRedirect: React.FC = () => {
  const { user, isAuthenticated, isLoading, checkAuthStatus } = useAuth()

  console.log('🔄 DashboardRedirect triggered:', { 
    user: user ? {email: user.email, role: user.role} : null, 
    isAuthenticated, 
    isLoading,
    timestamp: new Date().toISOString(),
    url: window.location.href
  })

  // Force a fresh auth check to ensure we have the latest session data
  React.useEffect(() => {
    if (!isLoading && isAuthenticated) {
      console.log('🔄 Forcing fresh auth check for role-based routing')
      checkAuthStatus()
    }
  }, [isLoading, isAuthenticated, checkAuthStatus])

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    console.log('🚫 Not authenticated or no user, redirecting to login')
    return <Navigate to="/login" replace />
  }

  // Debug logging to troubleshoot role-based routing
  console.log('🔍 DashboardRedirect Role-Based Routing:', {
    user: {
      email: user.email,
      role: user.role,
      status: user.status,
      id: user.id
    },
    roleType: typeof user.role,
    isAuthenticated,
    timestamp: new Date().toISOString()
  })

  // Get the exact role without modification to avoid any issues
  const userRole = user.role

  console.log('🎯 Directing user based on role:', {
    role: userRole,
    email: user.email,
    status: user.status
  })

  // Clear and explicit routing based on EXACT role match
  if (userRole === 'admin' || userRole === 'master_admin') {
    console.log(`✅ ${userRole.toUpperCase()} USER - Redirecting to /admin-dashboard`)
    return <Navigate to="/admin-dashboard" replace />
  }
  
  if (userRole === 'iso') {
    console.log('✅ ISO USER - Redirecting to /iso-dashboard')
    return <Navigate to="/iso-dashboard" replace />
  }
  
  if (userRole === 'broker') {
    console.log('✅ BROKER USER - Showing placeholder')
    return (
      <div className="container mt-5">
        <div className="alert alert-info text-center">
          <h4>Broker Dashboard Coming Soon</h4>
          <p>Broker dashboard is under development.</p>
          <p>Your role: <strong>{user?.role}</strong></p>
        </div>
      </div>
    )
  }

  // Unknown role
  console.error('❌ Unknown role or missing role:', userRole)
  return (
    <div className="container mt-5">
      <div className="alert alert-danger text-center">
        <h4>Role Not Recognized</h4>
        <p>Your account role is not recognized or missing.</p>
        <p>Role received: <strong>"{userRole}"</strong></p>
        <p>Expected: admin, master_admin, iso, or broker</p>
        <button 
          className="btn btn-primary mt-2"
          onClick={() => window.location.reload()}
        >
          Reload Page
        </button>
      </div>
    </div>
  )
}

const AppRouter: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<WelcomePage />} />
      <Route path="/login" element={<LoginForm />} />
      <Route path="/signup" element={<SignUpForm />} />
      <Route path="/complete-signup/:token" element={<AccountCompletionForm />} />
      <Route path="/reset-password" element={<ResetPasswordForm />} />

      {/* Dashboard Redirect Route */}
      <Route path="/dashboard" element={<DashboardRedirect />} />
      
      {/* Protected Routes - DOUBLE CHECK THESE ARE CORRECT */}
      <Route 
        path="/iso-dashboard" 
        element={
          <ProtectedRoute allowedRoles={['iso']}>
            <ISODashboard />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/admin-dashboard" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'master_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/admin/iso/:isoId" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'master_admin']}>
            <ISODetailPage />
          </ProtectedRoute>
        } 
      />

                <Route
            path="/submit-deal"
            element={
              <ProtectedRoute allowedRoles={['iso']}>
                <SubmitDealApplication />
              </ProtectedRoute>
            }
          />

      {/* Chatter Routes */}
      <Route 
        path="/chatter" 
        element={
          <ProtectedRoute allowedRoles={['iso']}>
            <ChatterPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/chatter/:opportunityId" 
        element={
          <ProtectedRoute allowedRoles={['iso']}>
            <ChatterPage />
          </ProtectedRoute>
        } 
      />


      {/* TODO: Add broker dashboard route */}
      <Route 
        path="/broker-dashboard" 
        element={
          <ProtectedRoute allowedRoles={['broker']}>
            <div className="container mt-5">
              <div className="alert alert-info text-center">
                <h4>Broker Dashboard Coming Soon</h4>
                <p>Broker dashboard is under development.</p>
              </div>
            </div>
          </ProtectedRoute>
        } 
      />

      {/* Catch all route - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default AppRouter